// Copyright 2025, University of Maryland, All Rights Reserved

/**
 * Shared text formatting utilities.
 */

import DOMPurify from 'dompurify'

/**
 * Escape HTML special characters for safe rendering.
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Defense-in-depth sanitizer for any HTML string headed for
 * dangerouslySetInnerHTML or document.write. Strips scripts, event handlers,
 * and javascript: URLs while preserving the formatting tags we actually use.
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } })
}

/**
 * Format ISO date string to localized date.
 */
export function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString()
}

/**
 * Format bytes into human-readable file size.
 * Returns null if bytes is null/undefined/0.
 */
export function formatFileSize(bytes: number | null | undefined): string | null {
  if (!bytes) return null
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Truncate text to a maximum length with ellipsis.
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Format a count with singular/plural label.
 */
export function formatCount(count: number, singular: string, plural?: string): string {
  const label = count === 1 ? singular : (plural ?? `${singular}s`)
  return `${count} ${label}`
}

/**
 * Extract the first markdown heading (# Title) from text.
 */
export function extractMarkdownTitle(markdown: string, fallback = 'Untitled'): string {
  const match = markdown.match(/^#\s+(.+)$/m)
  return match?.[1]?.trim() ?? fallback
}

/**
 * Convert markdown bold (**text**) to HTML <strong> after escaping.
 * Escapes HTML first, then converts bold markers.
 */
export function markdownBoldToHtml(text: string): string {
  const escaped = escapeHtml(text)
  return escaped.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
}
